import { Core } from "@faire/design-tokens";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  formatMoney,
  moneyOrZero,
  RoundingStrategy,
} from "@faire/web/common/money";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { faireBaseUrl } from "@faire/web/common/routes/faireBaseUrl";
import { Button } from "@faire/web/slate/Button";
import { Star } from "@faire/web/slate/icons/foundation/Star";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { getSpacing } from "@faire/web/slate/spacing/getSpacing";
import { Typography } from "@faire/web/slate/Typography";
import { NoTranslate } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import React from "react";
import styled from "styled-components";

import { BrandAvatar } from "@faire/retailer/components/BrandAvatar";
import { BrandBagButton } from "@faire/retailer/components/BrandBagButton/BrandBagButton";
import { ShowroomBadge } from "@faire/retailer/components/Showroom/ShowroomBadge";
import { Sticky } from "@faire/retailer/components/Sticky";
import { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { useStickyNavContentPositions } from "@faire/retailer/navbar/useStickyNavContentPositions";
import { trackBrandClick } from "@faire/retailer/services/Events";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { useTopOfPageContentStore } from "@faire/retailer/stores/ui/TopOfPageContentStore";
import { useBrandCartHeader } from "@faire/retailer/views/brand/BrandCart/BrandCartHeader/useBrandCartHeader";

import { generateBrandUrlForProductPage } from "../../utils/generateBrandUrlForProductPage";

interface IProps {
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  containerRef: React.RefObject<HTMLDivElement>;
  brandReviewSummary: IReviewSummary | undefined;
  showShowroomBadge: boolean;
  numberOfProductsInCart?: number;
  setCart?: () => void;
  showSidecart?: boolean;
  cartTotal?: IFaireMoney;
}

export const ProductStickyBrandHeader: React.FC<IProps> = ({
  product,
  brand,
  containerRef,
  brandReviewSummary,
  showShowroomBadge,
  numberOfProductsInCart,
  setCart,
  showSidecart,
  cartTotal,
}) => {
  const onBrandClick = () => {
    if (brand?.token !== undefined) {
      trackBrandClick(brand.token, "product_page_simple_brand_title");
    }
  };

  const { promoBannerHeight: topOfPageContentHeight } =
    useTopOfPageContentStore(["promoBannerHeight"]);

  const token = brand?.token;
  const brandPageUrl = token
    ? generateBrandUrlForProductPage(token, product?.token ?? "")
    : faireBaseUrl();
  const shouldShowTopShop =
    brand.is_top_shop && isLoggedInRetailerOrBrandPreview();

  const { shouldRetainOriginalPosition } = useStickyNavContentPositions(false);

  const { displayCurrency } = useRetailerStore(["displayCurrency"]);
  const { brandMinimumAmount } = useBrandCartHeader(token ?? "");
  const belowMinimumAmount = MoneyMath.subtract(
    brandMinimumAmount,
    moneyOrZero(cartTotal, displayCurrency)
  );

  return (
    <StyledSticky
      top={topOfPageContentHeight}
      containerRef={containerRef}
      $navbar={shouldRetainOriginalPosition}
    >
      <Row justify="space-between">
        <Row align="center">
          <Link onClick={onBrandClick} to={brandPageUrl}>
            {brand.profile_image?.url ? (
              <>
                <BrandAvatar
                  image={brand.profile_image}
                  size={shouldShowTopShop ? "x-small" : "smaller"}
                  isTopShop={shouldShowTopShop}
                  iconSize="22px"
                />
                <meta itemProp="image" content={brand.profile_image?.url} />
              </>
            ) : null}
          </Link>
          <Spacer width="1x" />
          <Button
            style={{ textDecoration: "none" }}
            variant="tertiary"
            onClick={onBrandClick}
            linkTo={brandPageUrl}
          >
            <NoTranslate>
              <Typography variant="paragraphSansMedium">
                {brand.name}
              </Typography>
            </NoTranslate>
          </Button>
          <MaybeBrandReviewSummary brandReviewSummary={brandReviewSummary} />
          <BrandMinimum
            minimumOrderAmount={brandMinimumAmount}
            total={moneyOrZero(cartTotal, displayCurrency)}
            amountBelowMinimum={belowMinimumAmount}
          />
          <MaybeShowroomBadge isShowroom={showShowroomBadge} />
        </Row>
        <BrandBagButton
          totalInCart={numberOfProductsInCart}
          setCart={setCart}
          showSidecart={showSidecart}
        />
      </Row>
    </StyledSticky>
  );
};

const MaybeBrandReviewSummary = ({
  brandReviewSummary,
}: {
  brandReviewSummary: IReviewSummary | undefined;
}) => {
  if (!brandReviewSummary) {
    return null;
  }

  return (
    <>
      <Spacer width="1x" />
      <Star
        fontSize="12px"
        fill={Core.icon.default}
        color={Core.icon.default}
      />
      <Spacer size={4} />
      <Typography variant="paragraphSansRegular">
        {brandReviewSummary.overallRatingText}
      </Typography>
    </>
  );
};

export const BrandMinimum: React.FC<{
  minimumOrderAmount: IFaireMoney;
  amountBelowMinimum: IFaireMoney;
  total: IFaireMoney;
}> = ({ minimumOrderAmount, amountBelowMinimum, total }) => {
  const { strictLocalize } = useStrictLocalization();
  const minimumInfo = () => {
    if (MoneyMath.isEqualToZero(minimumOrderAmount)) {
      return strictLocalize(
        {
          defaultMessage: "{minimumOrderAmountMoney} min",
          description: {
            text: "Minimum amount spent to order from this brand. Min is an abbreviation for minimum.",
            img: "https://cdn.faire.com/fastly/fadbc62124a61d656813f74bbd83710f5b455aa5fdca66fd262ac78db7a0e82a.png",
          },
        },
        {
          minimumOrderAmountMoney: formatMoney({
            money: minimumOrderAmount,
            roundingStrategy: RoundingStrategy.DROP_CENTS_ROUND_UP,
          }),
        }
      );
    } else if (MoneyMath.lessOrEqualToZero(amountBelowMinimum)) {
      return strictLocalize(
        {
          defaultMessage: "{minimumOrderMoney} min reached",
          description: {
            text: "Shown when brand minimum order amount has been reached. Min is an abbreviation for minimum.",
            img: "https://cdn.faire.com/fastly/3de02748f33dd02e69e1184b6035b0a406b075b4b9920ec49f6a53edb067661f.png",
          },
        },
        {
          minimumOrderMoney: formatMoney({
            money: minimumOrderAmount,
            omitCentsIfZero: true,
          }),
        }
      );
    } else if (!MoneyMath.isEqualToZero(total)) {
      return strictLocalize(
        {
          defaultMessage: "{belowMinimumMoney} to go",
          description: {
            text: "Shown when belowMinimumMoney is needed to meet the brand's minimum order amount. Min is an abbreviation for minimum.",
            img: "https://cdn.faire.com/fastly/701c56e6c4a82de44ecb10108e04aab52d83b71433005ef3a5079051a0e8fbef.png",
          },
        },
        {
          belowMinimumMoney: formatMoney({
            money: amountBelowMinimum,
            omitCentsIfZero: true,
          }),
        }
      );
    } else {
      return strictLocalize(
        {
          defaultMessage: "{minimumOrderAmountMoney} min",
          description: {
            text: "Minimum amount spent to order from this brand. Min is an abbreviation for minimum.",
            img: "https://cdn.faire.com/fastly/fadbc62124a61d656813f74bbd83710f5b455aa5fdca66fd262ac78db7a0e82a.png",
          },
        },
        {
          minimumOrderAmountMoney: formatMoney({
            money: minimumOrderAmount,
            roundingStrategy: RoundingStrategy.DROP_CENTS_ROUND_UP,
          }),
        }
      );
    }
  };
  return (
    <>
      <Spacer width="1x" />
      <Typography variant="paragraphSansRegular" color={Core.text.default}>
        {minimumInfo()}
      </Typography>
    </>
  );
};

const MaybeShowroomBadge = ({ isShowroom }: { isShowroom: boolean }) => {
  if (!isShowroom) {
    return null;
  }

  return (
    <>
      <Spacer width="1x" />
      <ShowroomBadge small />
    </>
  );
};

const StyledSticky = styled(Sticky)<{ $navbar: boolean }>`
  width: 100%;
  background-color: white;
  padding: ${getSpacing("2x", "6x")};
  z-index: 10;
`;
